import { ReactElement, useMemo } from 'react';
import classNames from 'classnames';

import { ThresholdMessage } from '@gn-audio/jss-core/site-settings/models';

import { useSiteSettings } from '../../../utils/contexts/SiteSettingsContext';
import useFallbackTranslation from '../../../utils/hooks/useFallbackTranslation';
import { useBasket } from '../BasketContext/useBasket';

import basketStyles from '../Basket.module.css';
import locationStyles from '../ShippingOutside/Location.module.css';

const ShippingMessage = (): ReactElement | null => {
  const { t } = useFallbackTranslation();
  const { basket } = useBasket();
  const { shipping } = useSiteSettings();

  const sortedThresholds = useMemo(() => {
    if (!shipping) return [];
    return shipping.thresholds
      .slice()
      .sort((a: ThresholdMessage, b: ThresholdMessage) => b.Threshold - a.Threshold);
  }, [shipping]);

  const message = useMemo(() => {
    if (!basket) {
      return null;
    }

    const { product_total } = basket;

    for (const { Threshold, Message } of sortedThresholds) {
      if (product_total >= Threshold) {
        return <>{Message}</>;
      }
    }
    return null;
  }, [sortedThresholds, basket]);

  return (
    message && (
      <div
        role="alert"
        aria-label="Shipping Message"
        aria-live="polite"
        className={classNames(
          'flex flex-wrap justify-center !rounded-none bg-beige-100 !p-3 !text-base !leading-7',
          basketStyles.card,
          locationStyles.container
        )}
      >
        <span className="font-normal">{t('cart.YourOrderQualifiesFor')}</span>
        <span className={classNames(basketStyles.fat, 'ml-2')} role="text">
          {message}
        </span>
      </div>
    )
  );
};

export default ShippingMessage;
